import Vue from 'vue'
import VueI18n from 'vue-i18n'
import moment from '@/moment-gp'
import messages, { numberFormats, dateTimeFormats } from './lang/en.js'
import elementLocale from 'element-ui/lib/locale'
import { availableLanguages } from '@/i18n/available-languages'
import en_elementLocale from 'element-ui/lib/locale/lang/en.js'

/*******************************************************************************
// TODO 1: Changing language back and forth doenst work (for elements ui)

// TODO 2: Dealing with 'en' vs. 'en-US'
            - Let user set a country code 'en-US'
            - check if available, otherwise map to 'en'

// TODO 3: Refactor
            - one asnyc load function
            - When Promise.all is fullfilled -> one setLanguage function
            - setLanuage sets i18n, moment, elements ui, etc.
            - expose setLanguage to public so it can be called to switch back
              and forth (see also TODO 1)

// TODO 4: Use cookies as fallback, when brower does not support local storage

*******************************************************************************/

/* our default language that is preloaded */
const loadedLanguages = ['en'];
const loadedElementLocales = {};

loadedElementLocales['en'] = en_elementLocale;

Vue.use(VueI18n)

var localStorage
if ( !process.browser ) {
  localStorage = {
    getItem() { return 'Europe/Berlin' }, /* This will cause a death spiral, when enabled in a browser! */
    setItem() {}
  };
} else {
  localStorage = window.localStorage;
}

const log = (...args) => { /* log(...args) */ };

/* Init Vue-i18n and set locale messages for englisch as default */
export const i18n = new VueI18n({
  locale: 'en',
  fallbackLocale: 'en',
  messages: { en: messages },
  numberFormats: { en: numberFormats },
  dateTimeFormats: { en: dateTimeFormats },
})

export let i18nSetUp = function () {

  if ( !localStorage.getItem('language') ) {
    /* Let's be optimistic and assume that the user configured the same language
       that moment.tz.guess will give us. If not we deal with that later... */
    localStorage.setItem('language', guess_language());
  } else {
    log("[i18n] Found language in local storage:", localStorage.getItem('language') );
  }

  /* Load user language */
  let lang = localStorage.getItem('language');
  loadLanguageAsync( lang );
  loadMomentLocaleAsync( lang );

  /* set moment lang */
  // moment.locale( lang );
}

function _setI18nLanguage( lang ) {
  i18n.locale = lang

  // Vue.config.lang = 'en' -> no offical config
  // axios.defaults.headers.common['Accept-Language'] = lang

  // TODO (used for emails)
  // firebase.auth().languageCode
  // firebase.auth().useDeviceLanguage();

  document.querySelector('html').setAttribute('lang', lang)

  return lang
}

function _setElementLanguage( lang ) {
  log("[i18n] setting element language", lang, "loaded:", loadedElementLocales);

  if ( lang in loadedElementLocales ) {
    elementLocale.use( loadedElementLocales[lang] );
  }
}

export function attachLanguageWatcher( store ) {

  let unwatch_user_get_language = store.watch(
    (state, getters) => getters["app/user_get_language"],
    user_language => {

      if ( !user_language ) {
        return;
      }

      log("[i18n] got new user_language", user_language);

      if ( localStorage.getItem('language') !== user_language ) {
        /* We have a problem: The user has a different language then we
          assumed, this means the store is fucked up */
        console.warn("[i18n] locally saved language does not match user language");

        /* Update local storage */
        localStorage.setItem('language', user_language);

        if ( location /* check if this is a browser */ ) {
          // TODO: Make absolutly sure, that we don't end up in a death sprial */
          location.reload()
        } else {
          throw new Error('Language mismatch detected. Language consistency can not be guaranteed. Reload.')
        }
      }

      _setI18nLanguage( user_language );

      if ( unwatch_user_get_language ) {
        log("[i18n] Self destroying watcher");
        unwatch_user_get_language(); // Self destroy
      }
    },
    { immediate: true }
  );
}

export function loadMomentLocaleAsync( lang ) {
  if ( lang === 'en') {
    /* english doesn't required to be loaded, it's the default */
    moment.locale('en');
    return Promise.resolve(false);
  }
  // TODO: We will get 'de', 'en', etc... but moment locales are 'de-at'.
  return import(
    /* webpackChunkName: "moment-lang-[request]" */
    `moment/locale/${lang}`
  ).then(locale => {
    // TODO Does not include momenz timezone data!
    log("got locale", locale);
    moment.locale(lang);
  })
}

export function loadLanguageAsync( lang ) {

  // lang = lang.toLowerCase(); // not required anymore
  log("[i18n] loadLanguageAsync", lang, "current:", i18n.locale);

  if (i18n.locale !== lang) {
    if (!loadedLanguages.includes(lang)) {

      if ( lang === 'en' ) {
        /* This will never happen since 'de' is always included in loaded languages */
      }

      if ( lang === 'zh-CN' ) {
        /* We are using a filter in vue.config.js:

        new webpack.NormalModuleReplacementPlugin(
          /element-ui[/\\]lib[/\\]locale[/\\]lang[/\\]zh-CN/,
          "element-ui/lib/locale/lang/en"
        )

        This means when we import zh-CN, it will always get replace by en
        */

        throw new Error('NOT IMPLEMENTED')
      }

      const pElement = import(
        /* webpackExclude: /element-ui[/\\]lib[/\\]locale[/\\]lang[/\\]zh-CN/ */
        /* webpackChunkName: "element-lang-[request]" */
        `element-ui/lib/locale/lang/${lang}.js`
      ).then(locale => {
        loadedElementLocales[ lang ] = locale.default;
        _setElementLanguage( lang )
      }).catch((e) => {
        log("Let me know if this ever happens", e);
      })

      const pLang = import(
        /* webpackChunkName: "i18n-lang-[request]" */
        `@/i18n/lang/${lang}`
      ).then(locale => {
        // TODO: setLocaleMessage?
        i18n.mergeLocaleMessage(lang, locale.default)
        if ( locale.numberFormats ) {
          i18n.mergeNumberFormat(lang, locale.numberFormats)
        }
        if ( locale.dateTimeFormat ) {
          i18n.mergeDateTimeFormat(lang, locale.dateTimeFormat)
        }
        loadedLanguages.push(lang)
        return _setI18nLanguage(lang)
      })

      return Promise.all([pElement, pLang]);
    }
    return Promise.resolve((() => {
      _setElementLanguage(lang); // HACK - it would be better to check loadedElementLocales
      _setI18nLanguage(lang);
      return '*already loaded*' // TODO
    })())
  }
  return Promise.resolve(lang)
}

export function getLoadedLanguages() {
  return loadedLanguages;
}

export function guess_language() {
  let lang = navigator.languages
        ? navigator.languages[0]
        : (navigator.language || navigator.userLanguage)

  if ( typeof lang === 'undefined' ) {
    lang = 'en'
  }
  lang = lang.toLowerCase();

  let langCodes = availableLanguages.map(lang => lang.value);

  if ( !langCodes.includes( lang ) ) {
    lang = 'en';
  }

  log("[i18n] autodetected language", lang);

  return lang;
}

// Call set up if we're in a browser environment
if ( process.browser ) {
  i18nSetUp();
}
